// RunsForTrailButton — Button + Bottom-Sheet Modal für die Trail-Detail-View
// (User-Wunsch Wave 14af): "neben 'Key sections' ein 'DH Runs'-Button mit
// Zähler-Badge, Klick öffnet Liste aller Runs die diesen Trail enthalten, Tap
// auf einen Run → /runs/:id".
//
// Warum als Button+Modal in EINER Komponente?
//   - Self-contained: MapPage gibt nur `trailId`, `runs`, `trailsById` rein,
//     der Rest (Open/Close-State, Navigation, Rendering) lebt hier.
//   - Parallele Sichtbarkeit mit CruxToggle, NavigateToTrailhead etc. bleibt
//     sauber — die Button-Reihe in MapPage wird nicht mit Modal-Logik
//     zugemüllt.
//   - Ein einzelner Import in MapPage.tsx.
//
// Styling-Anlehnung an CruxToggle:
//   - compact-Button mit 2-Zeilen-Label "DH" / "Runs", 18x18-Icon, count-
//     Badge oben rechts.
//   - Aktivierter Look (on) wird nicht gebraucht — der Button ist eher ein
//     "Link-öffnen"-Trigger und kein Toggle. Wir benutzen trotzdem den
//     on-Look wenn count > 0, damit das Auge ihn findet (User wollte das
//     Count-Badge sichtbar, und ein ganz-grauer Button mit Badge wirkt
//     "disabled").
//
// Modal = Fullscreen-Overlay mit Backdrop (z. B. 0.55 alpha schwarz) + Bottom-
// Sheet-Karte. Tap auf Backdrop oder auf ein Run-Item schließt das Modal.
// Kein react-modal o. ä. — wir bauen's inline mit Tailwind, analog zu den
// restlichen Rider-Overlays.

import { useEffect, useState } from 'react';
import { DIFFICULTY_LABEL, difficultySwatchBackground, type Difficulty } from '@adtu/shared';
import type { CachedRun } from '../lib/runs';
import type { CachedTrail } from '../lib/trails';
import { mapIntentsBus } from '../lib/map/mapIntentsBus';
import { DefenderIcon } from './icons/DefenderIcon';

export interface RunsForTrailButtonProps {
  /** ID des aktuell im Detail-Sheet selektierten Trails. */
  trailId: string;
  /** Alle lokal gecachten Runs (aus syncRunCache). */
  runs: CachedRun[];
  /**
   * Map trailId → CachedTrail für die Chain-Anzeige im Modal. Kommt aus
   * MapPage, wo wir eh schon `trails` haben.
   */
  trailsById: Map<string, CachedTrail>;
  /** Optional: Name des aktuellen Trails für die Modal-Headline. */
  trailName?: string;
}

/**
 * SVG: Mini-Version des Runs-Tab-Icons (siehe App.tsx IconRuns). "DH"
 * (Downhill) als Line-Buchstaben + kleiner Kringel pro Letter — Stil analog
 * zu IconRoute (Line-Icons mit Ring-Akzenten). Hier kleiner (18x18) und
 * nutzt `currentColor`, damit die Farbe vom Button-Text kommt (weiß/dim).
 */
function RunsIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      {/* D: Stave + Bowl */}
      <path d="M 4 7 L 4 19" />
      <path d="M 4 7 A 6 6 0 0 1 4 19" />
      <circle cx="4" cy="5.5" r="1.5" />
      {/* H: 2 Verticals + Crossbar */}
      <path d="M 13 7 L 13 19" />
      <path d="M 20 7 L 20 19" />
      <path d="M 13 13 L 20 13" />
      <circle cx="13" cy="5.5" r="1.5" />
    </svg>
  );
}

export function RunsForTrailButton({
  trailId,
  runs,
  trailsById,
  trailName,
}: RunsForTrailButtonProps) {
  const [open, setOpen] = useState(false);

  // Runs die diesen Trail enthalten. Stabile Sortierung: nach Run-Name.
  const matching = runs
    .filter((r) => r.trailIds.includes(trailId))
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
  const count = matching.length;

  // ESC schließt das Modal — nice-to-have für Browser-User, auf iOS ohne
  // physische Tastatur irrelevant aber nicht schädlich.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Wave 14ah: Run-Tap zeigt den Run DIREKT auf der Map an, statt erst den
  // RunDetailPage-Umweg zu gehen. Der User ist beim Tap eh schon auf der
  // Karte (wir rendern dieses Modal aus dem Trail-Detail-Sheet), also
  // reicht ein mapIntent — MapPage's Subscriber tauscht den selected Trail
  // gegen den Pseudo-Trail aus dem Run und fittet ihn ins Viewport.
  const handleRunTap = (runId: string) => {
    setOpen(false);
    mapIntentsBus.publish({ runId, resetMode: true });
  };

  // Label: Zwei Zeilen "DH / Runs" — passt in der Höhe zu "Key / sections".
  // Farb-Unterscheidung zu CruxToggle (Gelb-Amber für Hazard) — hier orange-
  // ähnlich zur Turbo-Brand, damit die Buttons nicht verwechselt werden. Bei
  // count=0 bleibt der Button dimmer, zeigt aber trotzdem Interaction-
  // Möglichkeit (kein disabled).
  const active = count > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (count === 0) return; // kein Modal öffnen wenn leer — kein Inhalt
          setOpen(true);
        }}
        title={
          count === 0
            ? 'This trail is not part of any DH run yet.'
            : `Show ${count} DH run${count === 1 ? '' : 's'} that include this trail`
        }
        aria-label={count === 0 ? 'No DH runs contain this trail' : `Show ${count} DH runs`}
        aria-disabled={count === 0}
        className={`flex-shrink-0 transition-colors px-2.5 py-1.5 rounded-lg flex items-center justify-center gap-1.5 border ${
          active
            ? 'bg-turbo-700/50 hover:bg-turbo-700/70 active:bg-turbo-700/90 text-white border-turbo-500/30'
            : 'bg-turbo-700/20 text-white/40 border-white/10 cursor-not-allowed'
        }`}
      >
        <span className="flex-shrink-0 relative">
          <DefenderIcon width={24} height={24} style={{ display: 'block' }} />
          {count > 0 && (
            <span
              className={`absolute -top-1.5 -right-2 min-w-[14px] h-[14px] px-0.5 text-[9px] leading-[14px] font-semibold rounded-full text-center ${
                active ? 'bg-orange-500 text-black' : 'bg-white/40 text-black/70'
              }`}
            >
              {count}
            </span>
          )}
        </span>
        <span className="text-[12px] font-medium leading-tight text-center">
          DH
          <br />
          Runs
        </span>
      </button>

      {open && (
        <RunsForTrailModal
          trailId={trailId}
          trailName={trailName}
          runs={matching}
          trailsById={trailsById}
          onClose={() => setOpen(false)}
          onSelect={handleRunTap}
        />
      )}
    </>
  );
}

/* --------------------------------------------------------------------------
 * Modal — Bottom-Sheet-Karte mit der Liste matchender Runs. Wird nur bei
 * open=true rendered, stoppt Event-Propagation auf dem Karten-Inhalt, damit
 * Tap außerhalb (auf Backdrop) sauber schließt.
 * -------------------------------------------------------------------------- */

interface ModalProps {
  trailId: string;
  trailName?: string;
  runs: CachedRun[];
  trailsById: Map<string, CachedTrail>;
  onClose: () => void;
  onSelect: (runId: string) => void;
}

function RunsForTrailModal({
  trailId,
  trailName,
  runs,
  trailsById,
  onClose,
  onSelect,
}: ModalProps) {
  return (
    // z-index: über dem Trail-Detail-Sheet (z-10/z-20-Raum). Wir nutzen
    // inline z-50 — die Sheet-Layer in MapPage liegt typisch unter z-40.
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end bg-black/55"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={trailName ? `DH runs containing ${trailName}` : 'DH runs containing this trail'}
    >
      <div
        className="bg-turbo-900/95 border-t border-turbo-700/60 rounded-t-2xl px-4 pt-3 pb-6 max-h-[75vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)' }}
      >
        {/* Grab-handle + Header */}
        <div className="flex justify-center mb-2">
          <span className="block w-10 h-1 rounded-full bg-white/25" aria-hidden="true" />
        </div>
        <div className="flex items-baseline justify-between mb-3 gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-white leading-tight">
              Downhill Runs
              <span className="text-xs text-white/45 font-normal tabular-nums ml-2">
                {runs.length}
              </span>
            </h2>
            {trailName && (
              <p className="text-[11px] text-white/50 mt-0.5 truncate">
                containing "{trailName}"
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white text-sm px-2 py-1 -mr-2 flex-shrink-0"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <ul className="flex flex-col gap-1.5 list-none m-0 p-0">
          {runs.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onSelect(r.id)}
                className="w-full text-left px-3 py-2.5 rounded-lg bg-turbo-800/60 hover:bg-turbo-700/60 active:bg-turbo-700/80 border border-turbo-700/40 flex items-start gap-3"
              >
                {/* Wave 14aj: weißer Halo via box-shadow, damit dunkle
                    Difficulty-Farben auf turbo-800-Karten sichtbar bleiben. */}
                <span
                  className="flex-shrink-0 w-1.5 h-10 rounded-sm"
                  style={{
                    background: difficultySwatchBackground(r.difficulty),
                    boxShadow: '0 0 0 1px rgba(255,255,255,0.35)',
                  }}
                  aria-label={DIFFICULTY_LABEL[r.difficulty as Difficulty]}
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-white truncate">
                    {r.name}
                  </span>
                  {/* Chain: "Trail1 → Trail2 → Trail3", aktueller Trail fett
                      + orange, damit der User sofort sieht wo "sein" Trail
                      in der Run-Kette sitzt. */}
                  <span className="block text-[11px] text-white/60 leading-snug mt-0.5">
                    {r.trailIds.map((tid, i) => {
                      const t = trailsById.get(tid);
                      const label = t?.name ?? `unknown`;
                      const isCurrent = tid === trailId;
                      return (
                        <span key={`${tid}-${i}`}>
                          {i > 0 && <span className="text-white/30"> → </span>}
                          <span
                            className={
                              isCurrent
                                ? 'text-orange-400 font-semibold'
                                : 'text-white/75'
                            }
                          >
                            {label}
                          </span>
                        </span>
                      );
                    })}
                  </span>
                </span>
                <span
                  className="text-white/30 text-lg flex-shrink-0 mt-1"
                  aria-hidden="true"
                >
                  ›
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
